import { useCallback, useEffect, useState } from "react";
import { updateDigitalTwin } from "../services/DigitalTwinService";

/**
 * Persist selected plan for a digital twin.
 * Wraps DigitalTwinService patch to set `selected_plan`.
 */
const usePlanSelection = ({
  digitalTwinId,
  initialSelectedPlanId = null,
  onSelectionPersisted,
  onError,
} = {}) => {
  const [selectedPlanId, setSelectedPlanId] = useState(
    initialSelectedPlanId || null
  );
  const [isSavingSelection, setIsSavingSelection] = useState(false);
  const [planSelectionError, setPlanSelectionError] = useState(null);

  useEffect(() => {
    setSelectedPlanId(initialSelectedPlanId || null);
  }, [initialSelectedPlanId]);

  const setPlanAsSelected = useCallback(
    async planId => {
      if (!digitalTwinId || isSavingSelection) {
        return null;
      }

      setIsSavingSelection(true);
      setPlanSelectionError(null);

      try {
        const nextSelection = planId || null;
        await updateDigitalTwin({ selected_plan: nextSelection }, digitalTwinId);
        setSelectedPlanId(nextSelection);
        if (onSelectionPersisted) {
          onSelectionPersisted(planId);
        }
        return planId;
      } catch (error) {
        setPlanSelectionError(error);
        if (onError) {
          onError(error);
        }
        throw error;
      } finally {
        setIsSavingSelection(false);
      }
    },
    [digitalTwinId, isSavingSelection, onSelectionPersisted, onError]
  );

  return {
    selectedPlanId,
    isSavingSelection,
    planSelectionError,
    setPlanAsSelected,
  };
};

export default usePlanSelection;
